import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Grid2 as Grid,
	Typography,
	TextField as Txt
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { nanoid } from 'nanoid';
import arrowleft from '../../Assets/Images/arrow-left.svg';
import DropDown from '../Common/drop-down.js';
import { TextField } from '../Common/textfield.js';
import { GREEN } from '../../Assets/Constants/theme.js';
import PrimaryButton from '../Common/Button/primary-button.js';
import InventoryTable from '../Common/inventory-table.js';
import customToast from '../Common/custom-toast.js';
import {
	getAvailableStatus,
	getInventoryData,
	getInventoryStateName,
	updateBulkInventory
} from '../../Services/api-config.js';
import GeneralModal from '../Common/Modal/GeneralModal/general-modal.js';
import { downloadPDF } from '../../utils/dowload-pdf.js';
import Loading from '../Common/LoaderComponent/loader.js';
import { getErrorMessage } from '../../utils/util.js';

const BulkInventory = () => {
	const navigate = useNavigate();
	const [stateName, setStateName] = useState('');
	const { stateId, denomination } = useParams();
	const allStatus = ['Available', 'Reserved', 'Blocked'];

	const [selectedStatus, setSelectedStatus] = useState('');
	const [quantity, setQuantity] = useState('');
	const [showTransitionTo, setShowTransitionTo] = useState(false);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [allDone, setAllDone] = useState(false);
	const [transitionTo, setTransitionTo] = useState('');
	const [totalCount, setTotalCount] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
	const [remark, setRemark] = useState('');
	const [status, setStatus] = useState([]);

	const [loader, setLoader] = useState(false);
	const { showToast } = customToast();

	const [gridColumns, setGridColumns] = useState([]);
	const [gridRows, setGridRows] = useState([]);

	const generateMenuItems = (status) => {
		const commonMenu = [];
		switch (status) {
		case 'Available': {
			return [...commonMenu, 'Blocked', 'Reserved', 'Used'];
		}

		case 'Blocked': {
			return [...commonMenu, 'Available'];
		}

		case 'Used': {
			return commonMenu;
		}

		case 'Reserved': {
			return [...commonMenu, 'Available', 'Used'];
		}

		default: {
			return commonMenu;
		}
		}
	};

	const handleRemarkDialogClose = () => {
		setOpenRemarkDialog(false);
		setRemark('');
	};

	const fetchData = async (payload = {}) => {
		setLoader(true);

		try {
			const { status, columns, data, message, error, totalCount }
				= await getInventoryData(stateId, {
					page: pageCount,
					stampDenomination: denomination,
					status: transitionTo || selectedStatus,
					limit: quantity,
					...payload
				});

			if (status === 200) {
				setTotalCount(totalCount);
				const tempColumns = columns.map((item) => ({
					...item,
					flex: 1,
					sortable: item?.sorting || false,
					filterable: false,
					disableColumnMenu: !item?.sorting
				}));
				const filteredColumns = tempColumns.filter(
					(column) =>
						column.field === 'stampCertificateId'
						|| column.field === 'articleCode'
				);

				setGridColumns(filteredColumns);
				setGridRows(data.map((item) => ({ ...item, id: nanoid() })));
				if (data.length > 0) {
					setShowTransitionTo(true);
				}
			} else {
				showToast('error', message || error);
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setLoader(false);
		}
	};

	const handleConfirmUpdate = async () => {
		setLoader(true);
		const temp = gridRows.map((item) => item.stampInventoryId);

		try {
			const res = await updateBulkInventory(transitionTo.toLowerCase(), temp, {
				remark
			});
			if (res.status === 200) {
				showToast('success', res.message || '');
				if (transitionTo === 'Used') {
					downloadPDF(
						'data:application/pdf;base64,' + res.base64Data,
						stateName
					);
				}

				setOpenRemarkDialog(false);
				setAllDone(true);
				navigate(`/inventory/${stateId}/${denomination}`);
			} else {
				showToast('error', getErrorMessage(res));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setLoader(false);
		}
	};

	const fetchStateName = async () => {
		try {
			const res = await getInventoryStateName(stateId);
			if (res.status === 200) {
				setStateName(res.stateName);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const fetchAvailableStatus = async () => {
		try {
			const res = await getAvailableStatus(stateId, denomination);
			if (res.status === 200) {
				const tempStatus = allStatus.filter((elem) => !res.data.data.includes(elem));
				setStatus(tempStatus);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getData = async () => {
		setLoader(true);
		try {
			await fetchStateName();
			await fetchAvailableStatus();
		} catch (error) {
			console.log(error);
		} finally {
			setLoader(false);
		}
	};

	const handleClick = () => {
		if (transitionTo === 'Used') {
			setOpenConfirmation(true);
		} else if (transitionTo) {
			setOpenRemarkDialog(true);
		} else {
			fetchData();
		}
	};

	useEffect(() => {
		getData();
	}, []);

	return (
		<Box width='99%' maxHeight='90vh' minHeight='90vh' position='relative'>
			<Loading open={loader} />
			<Stack sx={{ marginTop: '40px', height: '100%' }}>
				<Box display='flex' alignItems='center' justifyContent='space-between'>
					<Box
						display='flex'
						alignItems='center'
						gap='10px'
						ml='20px'
						mb='20px'
					>
						<Box
							component={'img'}
							src={arrowleft}
							style={{ cursor: 'pointer' }}
							onClick={() => {
								navigate(`/inventory/${stateId}/${denomination}`);
							}}
						/>
						<Typography>Back</Typography>
					</Box>
				</Box>
				<Box
					display='flex'
					justifyContent='space-between'
					width='99%'
					margin='auto'
					gap='20px'
					mb='20px'
				>
					<Box flex={1}>
						<TextField
							value={stateName}
							style={{ marginBottom: 20 }}
							labelClassName={'medium6'}
							placeholder={'State Name'}
							width={'100%'}
							removeImageLeftBorder={true}
							type={'text'}
							borderColor={'#ECF0E5'}
							name={'stateName'}
							required
							isColorChange={true}
							customStyle={{ opacity: 0.4 }}
							disabled
						/>
					</Box>
					<Box flex={1}>
						<TextField
							value={denomination}
							style={{ marginBottom: 20 }}
							labelClassName={'medium6'}
							placeholder={'Denomination'}
							width={'100%'}
							removeImageLeftBorder={true}
							type={'text'}
							borderColor={'#ECF0E5'}
							name={'denomination'}
							required
							isColorChange={true}
							customStyle={{ opacity: 0.4 }}
							disabled
						/>
					</Box>
				</Box>
				<Box
					display='flex'
					justifyContent='space-between'
					width='99%'
					margin='auto'
					gap='20px'
				>
					<Box flex={1}>
						<Typography>From</Typography>
						<DropDown
							className={'medium6'}
							width={'100%'}
							height={'40px'}
							placeholder={'Select Status'}
							dropdownValue={allStatus}
							disableMenuItem={status}
							name={'digitalStampStateId'}
							handleChange={(e) => {
								const value = e.target.value;
								setTransitionTo('');
								setShowTransitionTo(false);
								setGridColumns([]);
								setGridRows([]);
								setQuantity('');
								setSelectedStatus(value);
							}}
							dropDownSelected={selectedStatus}
							disabled={allDone}
						/>
					</Box>
					<Box flex={1}>
						<Typography>Quantity</Typography>
						<TextField
							value={quantity}
							style={{ marginBottom: 20 }}
							labelClassName={'medium6'}
							placeholder={'Quantity'}
							width={'100%'}
							removeImageLeftBorder={true}
							type={'number'}
							borderColor={'#ECF0E5'}
							name={'quantity'}
							required
							isColorChange={true}
							customStyle={{ opacity: 1 }}
							handleInput={(e) => {
								const value = e;
								if (value <= 50) {
									setQuantity(value);
								}
							}}
							disabled={allDone}
						/>
					</Box>
					{showTransitionTo && (
						<Box flex={1}>
							<Typography>Transition to</Typography>
							<DropDown
								className={'medium6'}
								width={'100%'}
								height={'40px'}
								placeholder={'Select Status'}
								value={transitionTo}
								dropdownValue={generateMenuItems(selectedStatus)}
								name={'digitalStampStateId'}
								handleChange={(e) => {
									const value = e.target.value;
									setTransitionTo(value);
								}}
								disabled={allDone}
							/>
						</Box>
					)}
				</Box>
				<GeneralModal
					open={openConfirmation}
					onClose={() => {
						setRemark('');
						setOpenConfirmation(false);
					}}
					btn2OnClick={() => {
						setOpenConfirmation(false);
						setOpenRemarkDialog(true);
					}}
					genText={'Important Note!'}
					genTextClassName={'medium1'}
					btn1Text={'No, Cancel'}
					btn1OnClick={() => {
						setRemark('');
						setOpenConfirmation(false);
					}}
					no2btn={false}
					btn1BorderColor={'#fff'}
					btn1Color={'#0A0909'}
					btn2Text={'Proceed'}
				>
					<p className='medium5' style={{ marginTop: '20px' }}>
						Downloading this Stamp Paper will permanently mark its status as{' '}
						<b>Used</b>, and you will no longer be able to download or modify
						its status.
					</p>
				</GeneralModal>

				<Box
					display='flex'
					justifyContent='flex-end'
					width='90%'
					margin='auto'
					py='10px'
					mb='20px'
				>
					{!allDone && (
						<PrimaryButton
							className={'semiBold14'}
							value={
								transitionTo
									? `Change to ${transitionTo}`
									: 'Search Availability'
							}
							backgroundColor={GREEN}
							borderRadius={'8px'}
							height='60px'
							width='200px'
							fontWeight={400}
							padding='10px 16px'
							textcolor={'#000'}
							disabled={quantity <= 0 || !selectedStatus}
							onClick={handleClick}
						/>
					)}
				</Box>

				<Dialog open={openRemarkDialog} onClose={handleRemarkDialogClose}>
					<DialogTitle>Add Remark</DialogTitle>
					<DialogContent>
						<Txt
							inputRef={(input) => input?.focus()}
							multiline
							rows={4}
							variant='outlined'
							fullWidth
							value={remark}
							onChange={(e) => setRemark(e.target.value)}
							sx={{ mb: 2 }}
						/>
						<Typography variant='body2' color='textSecondary'>
							Please provide your remarks before updating the status.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Box display='flex' alignItems='center' gap='10px' width='100%'>
							<Button
								variant='contained'
								id='gen-modal-btn'
								onClick={handleRemarkDialogClose}
								sx={{
									height: '20px',
									flex: 1,
									padding: '17px 20px',
									borderRadius: '8px',
									backgroundColor: 'transparent',
									border: '2px solid #b6d423',
									color: '#b6d423',
									boxShadow: 'none',
									marginTop: '20px',
									fontSize: '16px',
									'&:hover': {
										backgroundColor: 'transparent',
										boxShadow: 'none'
									}
								}}
							>
								<div className='medium5'>Cancel</div>
							</Button>

							<Button
								variant='contained'
								id='gen-modal-btn'
								onClick={handleConfirmUpdate}
								disabled={!remark}
								sx={{
									height: '40px',
									flex: 1,
									width: 'auto',
									padding: '17px 20px',
									borderRadius: '8px',
									backgroundColor: '#b6d423',
									color: '#000',
									boxShadow: 'none',
									marginTop: '20px',
									fontSize: '16px',
									'&:hover': {
										backgroundColor: '#b6d423',
										boxShadow: 'none'
									},
									'&.Mui-disabled': {
										backgroundColor: '#b6d423',
										opacity: '0.7'
									},
									'&.right-corner': {
										position: 'absolute',
										bottom: '20px',
										right: '20px'
									}
								}}
								s
							>
								<div className='medium5'>Update</div>
							</Button>
						</Box>
					</DialogActions>
				</Dialog>
				{gridRows.length > 0 && (
					<Grid container>
						<Grid sx={{ width: '100%', overflowX: 'scroll' }} size={12}>
							<Grid sx={{ minWidth: '1000px' }} size={12}>
								<InventoryTable
									rows={gridRows}
									columns={gridColumns}
									totalRowCount={totalCount}
									hasDuplicates={false}
									rowsPerPageRender={true}
									pageCount={pageCount}
									noPagination
									updatePageCount={(count) => {
										setPageCount(count);
									}}
									handleSortModelChange={(sort) => {
										const temp = {
											sortingColumnName: sort?.[0]?.field || '',
											sortingType: sort?.[0]?.sort || ''
										};
										if (
											temp.sortingColumnName === ''
											&& temp.sortingType === ''
										) {
											delete temp.sortingColumnName;
											delete temp.sortingType;
										}

										fetchData(temp);
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Stack>
		</Box>
	);
};

export default BulkInventory;
